import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  MenuItem,
  Alert,
  Dialog,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardHeader from "../Dashboard/DashboardHeader";
import {
  ApplicationType,
  entity_id,
  locationGeo,
  Platform,
} from "../Utils/Const";
import { baseapi } from "../Environmentalurl";
import useIPAddress from "../Utils/useIPAddress";
import api from "../Utils/Api";

export default function AddBeneficiary() {
  const navigate = useNavigate();
  const location = useLocation();
  const beneficiary = location.state?.beneficiary;
  const ip = useIPAddress();

  const [transferType, setTransferType] = useState("IMPS");
  const [showExtraFields, setShowExtraFields] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openTPIN, setOpenTPIN] = useState(false);
  const [tpin, setTpin] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [bankList, setBankList] = useState([]);
  const inputRefs = useRef([]);

  const [form, setForm] = useState({
    name: beneficiary?.name || "",
    bank: beneficiary?.bank || "",
    account: beneficiary?.acc || "",
    confirmAccount: beneficiary?.acc || "",
    ifsc: beneficiary?.ifsc || "",
    limit: "",
    mobile: "",
    mmid: "",
    receiverName: "",
    confirmMobile: "",
    mmidNumber: "",
    purpose: "",
    remarks: "",
  });

  // Load BankMaster from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem("BankMaster");
      if (raw) {
        const parsed = JSON.parse(raw);
        setBankList(Array.isArray(parsed) ? parsed : []);
      }
    } catch (e) {
      console.error("Failed to parse BankMaster from localStorage", e);
    }
  }, []);

  // ================= VALIDATE ACCOUNT =================
  const transferamount = async () => {
    try {
      setLoading(true);

      // ── MMID flow: skip IFSC & penny-drop, go straight to TPIN ──
      if (transferType === "MMID") {
        setShowExtraFields(true);
        setOpenTPIN(true);
        return;
      }

      // ── IMPS flow: validate IFSC, then penny-drop ──
      const ifscRes = await api.post(
        `${baseapi}/JanAadhaareWalletValidateIFSC`,
        {
          ReqType: "Jan_Aadhaar_eWallet_ValidateIFSC",
          IFSC: form.ifsc,
          MachineID: ip,
          Platform: Platform,
          ApplicationType: ApplicationType,
          EntityID: entity_id,
          GeoLocation: locationGeo,
          SessionID: sessionStorage.getItem("SessionID"),
        },
      );

      if (ifscRes.data?.Response_Code !== "1000") {
        alert("Invalid IFSC Code");
        return;
      }

      const bankName = ifscRes.data.Info?.[0]?.BANK || "";

      const verifyRes = await api.post(`${baseapi}/pennyDropvalidate`, {
        AcNumber: form.account,
        AcIFSC: form.ifsc,
      });

      if (!verifyRes.data?.isVerify) {
        alert("Account verification failed");
        return;
      }

      setForm((prev) => ({
        ...prev,
        name: verifyRes.data.Name || "",
        bank: bankName,
      }));

      setShowExtraFields(true);
      setOpenTPIN(true);
    } catch (err) {
      alert("Validation failed");
    } finally {
      setLoading(false);
    }
  };

  const addBeneficiary = async () => {
    if (!tpin) {
      alert("Please enter TPIN");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ApplicationType: ApplicationType,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        MachineID: ip,
        Mobilenumber: sessionStorage.getItem("mobileNumber"),
        Platform: Platform,
        ReqType: "Jan_Aadhaar_eWallet_Beneficiary",
        Statement: "AddBeneficiary",
        SessionID: sessionStorage.getItem("SessionID"),
        BeneficiaryType: transferType === "IMPS" ? "IFSC" : "MMID",
        BeneficiaryID: "",
        BeneficiaryName: form.name,
        BeneficiaryBank: form.bank,
        BeneficiaryAccountNumber: form.account,
        BeneficiaryIFSC: form.ifsc,
        BeneficiaryMobileNumber: form.mobile || "",
        BeneficiaryMMID: form.mmidNumber || "",
        TPIN: tpin,
        IMPS_IsVerification: "0",
      };

      const res = await api.post(
        `${baseapi}/janAadhaareWalletBeneficiary`,
        payload,
      );

      if (res.data?.Response_Code === "2680") {
        setSuccessMsg(res.data.Message);
        setTimeout(() => {
          navigate("/dashboard/transfertobank");
        }, 1500);
      } else {
        alert(res.data?.Message || "Failed to add beneficiary");
      }
    } catch (err) {
      alert("Something went wrong while adding beneficiary");
    } finally {
      setLoading(false);
      setOpenTPIN(false);
    }
  };

  const isAccountMismatch =
    form.confirmAccount.length > 0 && form.account !== form.confirmAccount;

  const isMobileMismatch =
    form.confirmMobile.length > 0 && form.mobile !== form.confirmMobile;

  return (
    <>
      <DashboardHeader />

      <Box bgcolor="#f6fafc" minHeight="100vh">
        <Box sx={{ maxWidth: "1200px", mx: "auto", px: 3, py: 5 }}>
          {successMsg && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMsg}
            </Alert>
          )}

          <Box display="flex" justifyContent="space-between">
            <Typography variant="h5" fontWeight="bold">
              Add Beneficiary
            </Typography>
            <Typography
              fontSize={13}
              color="primary"
              mb={1}
              sx={{ cursor: "pointer", fontWeight: 500 }}
              onClick={() => navigate("/dashboard/transfertobank")}
            >
              ← BACK TO TRANSFER
            </Typography>
          </Box>

          {/* ================= TRANSFER TYPE ================= */}
          <Box mb={3} mt={3}>
            <Typography fontSize={13} fontWeight={600} mb={1}>
              TRANSFER TYPE
            </Typography>
            <Box display="flex" gap={3}>
              <label>
                <input
                  type="radio"
                  value="IMPS"
                  checked={transferType === "IMPS"}
                  onChange={(e) => setTransferType(e.target.value)}
                />{" "}
                IMPS (IFSC)
              </label>
              <label>
                <input
                  type="radio"
                  value="MMID"
                  checked={transferType === "MMID"}
                  onChange={(e) => setTransferType(e.target.value)}
                />{" "}
                MMID
              </label>
            </Box>
          </Box>

          {/* ================= MAIN CONTENT ================= */}
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 8 }} item xs={12} md={8}>
              <Paper sx={{ p: 4, borderRadius: 3 }}>
                <Grid container spacing={3}>
                  {/* ===== IMPS FIELDS ===== */}
                  {transferType === "IMPS" && (
                    <>
                      <Grid size={{ xs: 12, md: 6 }} item xs={12} md={6}>
                        <Typography fontSize={13} fontWeight={600} mb={1}>
                          ACCOUNT NUMBER
                        </Typography>
                        <TextField
                          fullWidth
                          type="password"
                          value={form.account}
                          onChange={(e) =>
                            setForm({ ...form, account: e.target.value })
                          }
                          inputProps={{ inputMode: "numeric" }}
                          error={isAccountMismatch}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }} item xs={12} md={6}>
                        <Typography fontSize={13} fontWeight={600} mb={1}>
                          CONFIRM ACCOUNT NUMBER
                        </Typography>
                        <TextField
                          fullWidth
                          value={form.confirmAccount}
                          onChange={(e) =>
                            setForm({ ...form, confirmAccount: e.target.value })
                          }
                          inputProps={{ inputMode: "numeric" }}
                          error={isAccountMismatch}
                          helperText={
                            isAccountMismatch
                              ? "Account numbers do not match"
                              : ""
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 6 }} item xs={12}>
                        <Typography fontSize={13} fontWeight={600} mb={1}>
                          IFSC CODE
                        </Typography>
                        <TextField
                          fullWidth
                          value={form.ifsc}
                          onChange={(e) => {
                            let value = e.target.value.toUpperCase();

                            // Allow only letters and numbers
                            value = value.replace(/[^A-Z0-9]/g, "");

                            // Maximum 11 characters
                            if (value.length > 11) return;

                            // First 4 characters must be letters
                            if (value.length <= 4 && !/^[A-Z]*$/.test(value)) return;

                            // 5th character must be 0
                            if (value.length === 5 && value[4] !== "0") return;

                            setForm({ ...form, ifsc: value });
                          }}
                          inputProps={{ maxLength: 11 }}
                          error={form.ifsc.length > 0 && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc)}
                          helperText={
                            form.ifsc.length > 0 &&
                              !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc)
                              ? "Example: ABCD0123456"
                              : ""
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 6 }} item xs={6}>
                        <Typography fontSize={13} fontWeight={600} mb={1}>
                          PER TRANSACTION LIMIT
                        </Typography>
                        <TextField
                          fullWidth
                          value={form.limit}
                          onChange={(e) =>
                            setForm({ ...form, limit: e.target.value })
                          }
                        />
                      </Grid>
                    </>
                  )}

                  {/* ===== MMID FIELDS ===== */}
                  {transferType === "MMID" && (
                    <>
                      <Grid size={{ xs: 6 }} item xs={6}>
                        <Typography fontSize={13} fontWeight={600} mb={1}>
                          SELECT BANK
                        </Typography>
                        <TextField
                          select
                          fullWidth
                          value={form.bank}
                          onChange={(e) =>
                            setForm({ ...form, bank: e.target.value })
                          }
                        >
                          {bankList.length > 0 ? (
                            bankList.map((b) => (
                              <MenuItem key={b.Pk_Id} value={b.BankName}>
                                {b.BankName}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled value="">
                              No banks available
                            </MenuItem>
                          )}
                        </TextField>
                      </Grid>

                      <Grid size={{ xs: 6 }} item xs={6}>
                        <Typography fontSize={13} fontWeight={600} mb={1}>
                          BENEFICIARY NAME
                        </Typography>
                        <TextField
                          fullWidth
                          value={form.name}
                          onChange={(e) =>
                            setForm({ ...form, name: e.target.value })
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 6 }} item xs={6}>
                        <Typography fontSize={13} fontWeight={600} mb={1}>
                          MOBILE NUMBER
                        </Typography>
                        <TextField
                          fullWidth
                          value={form.mobile}
                          onChange={(e) =>
                            setForm({ ...form, mobile: e.target.value })
                          }
                          inputProps={{ inputMode: "numeric", maxLength: 10 }}
                          error={isMobileMismatch}
                        />
                      </Grid>

                      <Grid size={{ xs: 6 }} item xs={6}>
                        <Typography fontSize={13} fontWeight={600} mb={1}>
                          CONFIRM MOBILE NUMBER
                        </Typography>
                        <TextField
                          fullWidth
                          value={form.confirmMobile}
                          onChange={(e) =>
                            setForm({ ...form, confirmMobile: e.target.value })
                          }
                          inputProps={{ inputMode: "numeric", maxLength: 10 }}
                          error={isMobileMismatch}
                          helperText={
                            isMobileMismatch
                              ? "Mobile numbers do not match"
                              : ""
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 6 }} item xs={12}>
                        <Typography fontSize={13} fontWeight={600} mb={1}>
                          MMID NUMBER
                        </Typography>
                        <TextField
                          fullWidth
                          value={form.mmidNumber}
                          onChange={(e) =>
                            setForm({ ...form, mmidNumber: e.target.value })
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 6 }} item xs={12}>
                        <Typography fontSize={13} fontWeight={600} mb={1}>
                          PER TRANSACTION LIMIT
                        </Typography>
                        <TextField
                          fullWidth
                          value={form.limit}
                          onChange={(e) =>
                            setForm({ ...form, limit: e.target.value })
                          }
                        />
                      </Grid>
                    </>
                  )}

                  {/* ===== EXTRA FIELDS AFTER IMPS VALIDATION ===== */}
                  {showExtraFields && transferType === "IMPS" && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Beneficiary Name"
                          value={form.name}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Bank"
                          value={form.bank}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid
                    size={{ xs: 12 }}
                    item
                    xs={12}
                    display="flex"
                    justifyContent="center"
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={transferamount}
                      disabled={loading}
                      sx={{ color: "#ffffff" }}
                    >
                      {loading ? "VALIDATING..." : "VALIDATE ACCOUNT"}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* ===== RIGHT SIDEBAR ===== */}
            <Grid size={{ xs: 12, md: 4 }} item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: "#0f172a",
                  color: "#fff",
                  mb: 3,
                  boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
                }}
              >
                <Typography fontWeight="bold" mb={1}>
                  Secure Transaction
                </Typography>
                <Typography fontSize={14}>
                  • Encrypted using 256-bit SSL
                </Typography>
                <Typography fontSize={14}>
                  • Verified via ₹1 Penny Drop
                </Typography>
                <Typography fontSize={14}>• RBI compliant process</Typography>
              </Paper>

              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography fontWeight="bold" mb={2}>
                  Limits Summary
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Transaction Limit:</Typography>
                  <Typography fontWeight="bold">Rs.50,000</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Monthly Limit per beneficiary:</Typography>
                  <Typography fontWeight="bold">Rs.2,00,000</Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* ================= TPIN DIALOG ================= */}
      <Dialog open={openTPIN} onClose={() => setOpenTPIN(false)}>
        <Box p={3} width={350}>
          <Typography fontWeight="bold" mb={2}>
            Enter TPIN
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            {[0, 1, 2, 3].map((index) => (
              <TextField
                key={index}
                type="password"
                value={tpin[index] || ""}
                inputRef={(el) => (inputRefs.current[index] = el)}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(-1);

                  const arr = tpin.padEnd(6, " ").split("");
                  arr[index] = value;

                  setTpin(arr.join("").trim());

                  // Move to next box
                  if (value && index < 5) {
                    inputRefs.current[index + 1]?.focus();
                  }
                }}
                onKeyDown={(e) => {
                  // Move to previous box on backspace
                  if (e.key === "Backspace" && !tpin[index] && index > 0) {
                    inputRefs.current[index - 1]?.focus();
                  }
                }}
                inputProps={{
                  maxLength: 1,
                  inputMode: "numeric",
                  style: {
                    textAlign: "center",
                    fontSize: "20px",
                    fontWeight: 600,
                  },
                }}
                sx={{
                  width: 55,
                  "& .MuiOutlinedInput-root": {
                    height: 55,
                    borderRadius: "12px",
                  },
                }}
              />
            ))}
          </Box>
          <Box display="flex" gap={2} mt={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setOpenTPIN(false)}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={addBeneficiary}
              disabled={loading}
            >
              {loading ? "PROCESSING..." : "CONFIRM"}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
